import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { logError } from '../../../../utils/logger';
import { useAuth } from '../../../auth/auth.context';
import { ControlledTextField } from '../../../shared/components';
import { submitFeedback } from '../feedback.service';

const DEFAULT_VALUES = { subject: '', message: '' };

/**
 * FeedbackForm
 *
 * Allows an authenticated employee to submit feedback or queries.
 *
 * Responsibilities:
 * - Capture subject and message input
 * - Submit feedback via service layer
 * - Provide user feedback on success or failure
 * - Trigger parent refresh after successful submission
 *
 * Props:
 * @param {Function} onFeedbackSubmitted
 *   Callback invoked after successful submission to refresh feedback list.
 *
 * Notes:
 * - Requires authentication context to resolve employee identity.
 * - Uses react-hook-form for local form state management.
 */
export default function FeedbackForm({ onFeedbackSubmitted }) {
  const { user } = useAuth();
  const employeeId = user?.user_id;

  const [updating, setUpdating] = useState(false);

  const methods = useForm({ defaultValues: DEFAULT_VALUES });
  const { handleSubmit, reset } = methods;

  const handleFeedbackSubmission = async (data) => {
    if (!employeeId) return;
    setUpdating(true);
    try {
      await submitFeedback(employeeId, data);
      toast.success('Feedback shared successfully.');
      reset(DEFAULT_VALUES);
      onFeedbackSubmitted?.(true);
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to share feedback',
          error: err,
          code: 'FEEDBACK_SUBMIT_FAILED',
        })
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form className='column' onSubmit={handleSubmit(handleFeedbackSubmission)}>
        <p>Enter your query/feedback</p>

        <div className='column gap-1'>
          <ControlledTextField
            id='subject'
            name='subject'
            type='text'
            placeholder='Enter subject'
            rules={{ required: 'Feedback subject is required' }}
            fullWidth
          />

          <ControlledTextField
            id='message'
            name='message'
            type='text'
            placeholder='Enter content'
            rules={{
              required: 'Feedback is required',
              maxLength: { value: 1000, message: 'Feedback cannot exceed 1000 characters' },
            }}
            isMultiline
            rows={5}
            showCharCount
          />
        </div>
        <div className='row gap-3 self-end'>
          <button
            type='button'
            className='btn outlined-btn'
            onClick={() => reset(DEFAULT_VALUES)}
            disabled={updating}
          >
            Cancel
          </button>

          <button type='submit' className='btn cta-btn' disabled={updating}>
            Submit
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
